import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts, ProTip } from '../../components/LessonUI';

const performanceApiCode = `// Architectural Logic: Precision Measurement
// Using the High-Resolution Time API
const start = performance.now();

// Execute complex logic
processExtractedData(rawData);

const end = performance.now();
console.log(\`Execution Identity: \${(end - start).toFixed(4)}ms\`);

// Performance Marks for Timeline Analysis
performance.mark('data-extraction-start');
// ... logic ...
performance.mark('data-extraction-end');

performance.measure(
    'Extraction Metric', 
    'data-extraction-start', 
    'data-extraction-end'
);`;

const lighthouseMetricsCode = `// Performance Budget: Core Web Vitals
// LCP: Largest Contentful Paint (Loading) - Target: < 2.5s
// FID: First Input Delay (Interactivity) - Target: < 100ms
// CLS: Cumulative Layout Shift (Stability) - Target: < 0.1

// Monitoring Long Tasks (> 50ms)
const observer = new PerformanceObserver((list) => {
    list.getEntries().forEach((entry) => {
        console.warn("Main Thread Blockage detected:", entry.name);
        console.log("Duration:", entry.duration, "ms");
    });
});

observer.observe({ entryTypes: ['longtask'] });`;

const flameGraphCode = `// Analyzing the Flame Graph
// 1. Identify "Long Tasks" marked with red flags.
// 2. Look for "Sawtooth" patterns in Memory view (GC pressure).
// 3. Pinpoint deep call stacks that block the main thread.

// Optimization: Breaking up long tasks with setTimeout(0) or requestIdleCallback
function chunkProcessing(data) {
    if (data.length === 0) return;
    
    const chunk = data.splice(0, 100);
    process(chunk);
    
    // Yield control back to the browser
    setTimeout(() => chunkProcessing(data), 0);
}`;

const JSProfiling = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Profiling & Metrics</div>
                <h1>JS Profiling: Engineering for Speed</h1>
                <p className="hero-lead">
                    Moving beyond "it feels fast." Master the scientific approach to performance
                    using high-resolution timing, Flame Graph analysis, and the Core Web Vitals
                    metrics that define modern SEO and user success.
                </p>
            </div>

            <div className="content-section">
                <h2>The Precision of Performance.now()</h2>
                <p>
                    Standard `Date.now()` is susceptible to system clock drift and only offers
                    millisecond precision. For engineering-grade profiling, we use the **User
                    Timing API**, which provides microsecond-accurate data and allows for
                    named measurements that appear directly in the browser's Performance tab.
                </p>
                <CodeBlock code={performanceApiCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>The Performance Budget: Core Web Vitals</h2>
                <p>
                    Performance isn't just about total load time. It's about perception. Google's
                    **Core Web Vitals** provide a structured framework for measuring loading,
                    interactivity, and visual stability.
                </p>
                <CodeBlock code={lighthouseMetricsCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Mastering the Flame Graph</h2>
                <p>
                    The Flame Graph in Chrome DevTools is the most powerful tool for diagnosing
                    **Main Thread Blockage**. It visualizes every function call over time. High
                    latency is often caused by single functions that exceed the 50ms "Long Task"
                    threshold, causing UI stutter.
                </p>
                <CodeBlock code={flameGraphCode} language="javascript" />
            </div>

            <ProTip><p>
                    To achieve a fluid 60FPS experience, the browser has approximately 16.6ms
                    to perform all calculations and render a frame. If your JavaScript execution
                    takes 10ms, you only have 6.6ms left for the browser to perform layout
                    and painting. Aim for "JS Budget" of &lt; 10ms per frame.
                </p></ProTip>

            <KeyFacts><ul>
                    <li>**Lighthouse:** Run mobile-first audits to see real-world performance throttled.</li>
                    <li>**Tracing:** Use "Record" in the Performance tab to find specifically which function is slow.</li>
                    <li>**Layout Thrashing:** Avoid reading properties like `offsetHeight` immediately after setting styles.</li>
                    <li>**Main Thread:** Move heavy computations (Image processing, Big Data) to **Web Workers**.</li>
                    <li>**Interaction:** Monitor FID to ensure buttons respond immediately to user clicks.</li>
                    <li>**Memory Snapshots:** Take heap snapshots before and after complex operations to find leaks.</li>
                </ul></KeyFacts>

        </div>
    );
};

export default JSProfiling;


